// OnshapeTagAudit — every part in this element, and whether it is tagged.
//
// Alex, 2026-08-21, after testing all seven Location×Context combinations: no
// selection-driven panel can also be the document panel, and none of them shows a
// part and a sub-assembly at once. So the panel stops chasing the selection and
// reads the WHOLE ELEMENT — more informative, and the same single call.
//
// His plan, in his order: "First, only the get, with the push of a button…
// Second, prepare what a push could look like and do (test with a button). Then
// add the push on a button." This is step one. NOTHING here writes to Onshape;
// rule 10 keeps writes unauthorized until he names the field set, and the point
// of this card is to let him decide that from evidence.
//
// ⚠ TWO-PHASE, ALWAYS (rule 10). Prepare states the cost, Load spends it with
// that number as an explicit budget. The server refuses a load with no budget, so
// this is not merely a UI courtesy — and the card must never auto-load on mount,
// which is exactly how a panel turns into a quota leak.
//
// THE STATES ARE DIFFERENT REPAIRS, NOT SEVERITIES — that is why they are counted
// separately rather than rolled into "N problems":
//   tagged     nothing to do
//   misplaced  the id exists but sits in the part number or config, not db_id.
//              The work was done; it landed one field over (three times, hence
//              the guard that names the field).
//   malformed  db_id holds something that is not <kind>:<number>
//   stale      a well-formed id naming a row that no longer exists
//   untagged   no id anywhere — the only state that needs a decision from Alex
import { useState, useCallback, useEffect } from 'react';
import Card from './Card.jsx';
import Notice from './Notice.jsx';
import { dbHrefFor } from '../nav-link.js';
import { BTN_PRIMARY, BTN_SECONDARY, BTN_DISABLED, NAVY } from '../theme-tokens.js';
import { LINK, OK, WARN } from '../icons.js';

const CELL = { fontSize: 'var(--fs-small)', padding: '2px 6px 2px 0', verticalAlign: 'top' };
const LINK_STYLE = { color: NAVY, fontWeight: 600, textDecoration: 'underline' };

// Word first, mark second — Alex is colour-blind, so the state must survive being
// read in greyscale.
const STATE = {
  tagged: { word: 'tagged', mark: OK },
  misplaced: { word: 'wrong field', mark: WARN },
  malformed: { word: 'malformed', mark: WARN },
  stale: { word: 'stale id', mark: WARN },
  untagged: { word: 'untagged', mark: '—' },
  // Alex's own "not a DB item" marker (N/A, fam:na). An answer, not a fault — so
  // it reads as settled and is not counted toward "to fix".
  excluded: { word: 'not a DB item', mark: OK },
};

// Minutes is the finest useful grain, same as SyncWorklistSection's own stamp:
// the question is "how old is this answer", not which second it was written.
function stamp(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

const hrefFor = (r) => {
  if (!r?.resolved) return null;
  const v = r.resolved;
  if (v.kind === 'product') return dbHrefFor('products', `c3-${v.category_id}|${v.id}`);
  return dbHrefFor('catalog', `c3-${v.category_id}|fam-${v.id}`);
};

export default function OnshapeTagAudit({ onshapeRef, storageKey }) {
  const [plan, setPlan] = useState(null);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // ⚠ THIS RUNS ON OPEN, AND IT IS SAFE ONLY BECAUSE IT COSTS NOTHING.
  // The no-confirm branch is planTakeoff (parses the ref) plus peekTakeoff (never
  // fetches) — zero Onshape calls — so it can auto-run where a live read never
  // could. Alex paid twice for one answer that was on disk the whole time; this is
  // what shows it. If this branch ever gains a live call, this effect must go with
  // it, or the panel becomes the quota leak the header warns about.
  const prepare = useCallback(() => {
    if (!onshapeRef) return;
    setBusy(true); setError(null);
    fetch(`/api/onshape/tag-audit?ref=${encodeURIComponent(onshapeRef)}`)
      .then(r => r.json().then(b => { if (!r.ok) throw new Error(b.error || `HTTP ${r.status}`); return b; }))
      .then(b => { setPlan(b.plan); if (b.cached) setData(b.cached); setBusy(false); })
      .catch(e => { setError(e.message); setBusy(false); });
  }, [onshapeRef]);

  useEffect(() => { setData(null); setPlan(null); prepare(); }, [prepare]);

  // The budget passed here is the number the user just read on the button. It is
  // not recomputed, on purpose: the guard is the human action, not the arithmetic.
  const load = useCallback(() => {
    if (!onshapeRef || !plan) return;
    setBusy(true); setError(null);
    const n = plan.identityCalls ?? plan.cap ?? 0;
    fetch(`/api/onshape/tag-audit?ref=${encodeURIComponent(onshapeRef)}&confirm=1&budget=${n}`)
      .then(r => r.json().then(b => { if (!r.ok) throw new Error(b.error || `HTTP ${r.status}`); return b; }))
      .then(b => { setData(b); setBusy(false); })
      .catch(e => { setError(e.message); setBusy(false); });
  }, [onshapeRef, plan]);

  const counts = data?.counts || {};
  // `excluded` and `tagged` are both settled answers — only genuine repairs count.
  const needWork = (counts.untagged || 0) + (counts.misplaced || 0) + (counts.malformed || 0) + (counts.stale || 0);

  return (
    <Card
      storageKey={storageKey}
      title="Tag audit"
      count={data ? `${data.total} part${data.total !== 1 ? 's' : ''}${needWork ? ` · ${needWork} to fix` : ' · all tagged'}` : undefined}
      applicable={!!onshapeRef}
      naReason="Link this element to a Core row first — the audit reads the element the link names."
    >
      {error && <Notice kind="error" onDismiss={() => setError(null)}>{error}</Notice>}

      {!data && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontSize: 'var(--fs-small)', color: 'var(--th-text-secondary)' }}>
            Reads every part in this element in one go — a part studio and an assembly both
            cost the same single read, and nothing is written.
          </div>
          {!plan ? (
            <button onClick={prepare} disabled={busy || !onshapeRef} style={{ ...BTN_SECONDARY, ...(busy ? BTN_DISABLED : null) }}>
              {busy ? 'Checking…' : 'Prepare (check cost)'}
            </button>
          ) : (
            <button onClick={load} disabled={busy} style={{ ...BTN_PRIMARY, ...(busy ? BTN_DISABLED : null) }}>
              {busy ? 'Reading…' : `Read this element — up to ${plan.identityCalls ?? plan.cap} call${(plan.identityCalls ?? plan.cap) !== 1 ? 's' : ''}`}
            </button>
          )}
        </div>
      )}

      {data && (
        <>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 6, fontSize: 'var(--fs-small)' }}>
            <span style={{ color: 'var(--th-text-secondary)' }}>{data.kind}</span>
            {data.readAt && (
              <span style={{ color: 'var(--th-text-hint)' }} title={data.readAt}>
                read {stamp(data.readAt)}
              </span>
            )}
            {Object.entries(counts).map(([k, v]) => (
              <span key={k} style={{ color: k === 'tagged' ? 'var(--th-text-secondary)' : 'var(--th-text-body)', fontWeight: k === 'tagged' ? 400 : 700 }}>
                {v} {STATE[k]?.word || k}
              </span>
            ))}
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <tbody>
                {data.rows.map((r, i) => (
                  <tr key={i} style={{ borderTop: '1px solid var(--th-border-subtle)' }}>
                    <td style={{ ...CELL, whiteSpace: 'nowrap' }}>
                      {r.viewHref
                        ? <a href={r.viewHref} target="_blank" rel="noopener noreferrer" style={LINK_STYLE}>{r.name || '—'} {LINK}</a>
                        : (r.name || '—')}
                    </td>
                    <td style={{ ...CELL, fontFamily: 'var(--ff-mono)', color: 'var(--th-text-hint)', whiteSpace: 'nowrap' }}>
                      {r.part_number || ''}
                    </td>
                    {/* The quantity the BOM card used to show (2026-10-13: the panel's
                        BOM card and this one read the same element; one read now
                        answers both). Instances on an assembly, group size on a
                        part studio — the same number the sync starts from. */}
                    <td style={{ ...CELL, whiteSpace: 'nowrap', textAlign: 'right' }}>
                      {r.qty != null ? `× ${r.qty}` : ''}
                    </td>
                    <td style={{ ...CELL, whiteSpace: 'nowrap', fontWeight: r.state === 'tagged' ? 400 : 700 }}>
                      {STATE[r.state]?.mark} {STATE[r.state]?.word}
                      {!r.categorized && (
                        <div style={{ color: 'var(--th-status-draft)', fontWeight: 700 }}
                             title="No $ Pricing category — the sync skips this part entirely, however good its db_id is.">
                          {WARN} no category
                        </div>
                      )}
                    </td>
                    <td style={{ ...CELL }}>
                      {r.resolved
                        ? <a href={hrefFor(r)} target="_blank" rel="noopener noreferrer" style={LINK_STYLE}>{r.resolved.code} · {r.resolved.label} {LINK}</a>
                        : <span style={{ color: 'var(--th-text-hint)' }}>{r.hint || r.db_id_raw || ''}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ⚠ THE SECOND AXIS, AND IT IS THE ONE THAT BIT HARDEST. A part with no
              `$ Pricing` category is skipped ENTIRELY by the sync — not priced and
              not reported, because every flags() branch is guarded on kind. So a row
              can read "tagged" above and still be invisible; that is exactly what
              happened to TTC Turn Table Conc. after it had been correctly tagged
              (ON-40). It gets its own line rather than a state, because it is a
              different question from "does this part carry an id". */}
          {data.uncategorized > 0 && (
            <div style={{ marginTop: 8 }}>
              <Notice kind="warning">
                <b>{data.uncategorized}</b> part{data.uncategorized !== 1 ? 's carry' : ' carries'} no
                {' '}<code>$ Pricing</code> category, so the sync skips {data.uncategorized !== 1 ? 'them' : 'it'} entirely —
                neither priced nor reported, even when the <code>db_id</code> is correct.
              </Notice>
            </div>
          )}

          {/* STEP TWO OF ALEX'S PLAN: "prepare what a push could look like and do
              (test with a button)". This is the dry run — it writes NOTHING, costs
              NO extra call (it is computed from the read already in hand), and
              exists so the write set can be judged from real rows before rule 10's
              authorization is given. Only the derivable repair is proposed: the id
              is already ON the part, in the wrong field. */}
          {data.pushPlan?.count > 0 && (
            <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid var(--th-border-subtle)' }}>
              <div style={{ fontSize: 'var(--fs-small)', fontWeight: 700, color: 'var(--th-text-body)', marginBottom: 4 }}>
                What a push would do — {data.pushPlan.count} part{data.pushPlan.count !== 1 ? 's' : ''}, {data.pushPlan.calls} call
              </div>
              <div style={{ fontSize: 'var(--fs-small)', color: 'var(--th-text-secondary)', marginBottom: 6 }}>
                Nothing is written. These ids are already on the parts — only in the wrong
                field — so the repair needs no decision from you.
              </div>
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <tbody>
                  {data.pushPlan.items.map((p, i) => (
                    <tr key={i}>
                      <td style={{ ...CELL, whiteSpace: 'nowrap', fontFamily: 'var(--ff-mono)' }}>{p.property}</td>
                      <td style={{ ...CELL, fontFamily: 'var(--ff-mono)', fontWeight: 700 }}>{p.value}</td>
                      <td style={{ ...CELL, color: 'var(--th-text-hint)' }}>{p.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ marginTop: 6, fontSize: 'var(--fs-small)', color: 'var(--th-text-hint)' }}>
                No write button yet — Onshape writes stay unauthorized until you name the field set (rule 10).
              </div>
            </div>
          )}

          {/* Re-read spends a call, so it says so on the button rather than
              blanking the table first — the old version dropped what you were
              reading to show a Prepare step you had already done. */}
          <div style={{ marginTop: 8, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <button onClick={load} disabled={busy || !plan} style={{ ...BTN_SECONDARY, ...(busy || !plan ? BTN_DISABLED : null) }}>
              {busy ? 'Reading…' : `Read again — up to ${plan?.identityCalls ?? plan?.cap ?? '?'} call${(plan?.identityCalls ?? plan?.cap) !== 1 ? 's' : ''}`}
            </button>
          </div>
        </>
      )}
    </Card>
  );
}
